import { Component, computed, inject, Renderer2, signal, ViewChild, type OnInit, type OnDestroy } from '@angular/core';
import { Router, NavigationEnd, RouterOutlet, RouterLink } from '@angular/router';
import { type Subscription, filter } from 'rxjs';
import { LayoutService } from '../../core/layout.service';
import { MenuService, type TabCloseEvent } from '../../core/menu.service';
import { NgClass } from '@angular/common';
import { MenuModule } from 'primeng/menu';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { type MenuItem } from 'primeng/api';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TechReportService } from '@app/core/tech-reports.service';
import { PopoverModule } from 'primeng/popover';
import { ButtonModule } from 'primeng/button';
import { ChatService } from '@app/core/chat.service';
import { SplitterModule } from 'primeng/splitter';
import { DrawerModule } from 'primeng/drawer';
import { SelectButton } from 'primeng/selectbutton';
import { InboxService } from '@app/core/inbox.service';
import { BadgeModule } from 'primeng/badge';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { DateToNowPipe } from "@app/pipes/dateToNow.pipe";
import { BreadcrumbComponent } from '@app/components/breadcrumb/breadcrumb.component';
import { SessionService } from '@app/core/session.service';
import { NumberPipe } from '@app/pipes/number.pipe';
import { MeterGroupModule } from 'primeng/metergroup';
import { UsageService } from '@app/core/usage.service';
import { CreditMeterComponent } from '@app/components/credit-meter/credit-meter.component';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ApiService } from '@app/core/api.service';
import { Checkbox } from 'primeng/checkbox';
import { WorkspaceService } from '@app/core/workspace.service';
import { IdenticonComponent } from "@app/components/identicon/identicon.component";
import { InitialsAvatarComponent } from "@app/components/initials-avatar/initials-avatar.component";
import { type IOrganizationSummary } from '@app/core/session.service';
import { version } from '../../../../package.json';
import { InboxDrawerComponent } from '@app/components/inbox-drawer/inbox-drawer.component';
import { CreateOrgDialogComponent } from '@app/components/create-org-dialog/create-org-dialog.component';
@Component({
  selector: 'app-base-layout',
  standalone: true,
  imports: [
    NgClass, RouterOutlet, MenuModule, ToggleSwitchModule, SplitterModule, SelectButton,
    RouterLink, FormsModule, PopoverModule, ButtonModule, DrawerModule,
    OverlayBadgeModule, BadgeModule, DateToNowPipe, BreadcrumbComponent, NumberPipe,
    MeterGroupModule, CreditMeterComponent, DialogModule, FormsModule, ReactiveFormsModule, InputTextModule, Checkbox,
    IdenticonComponent,
    InitialsAvatarComponent,
    InboxDrawerComponent,
    CreateOrgDialogComponent,
],
  templateUrl: './base-layout.component.html',
  styleUrl: './base-layout.component.scss'
})
export class BaseLayoutComponent implements OnInit, OnDestroy {
  layoutService = inject(LayoutService);
  menuService = inject(MenuService);
  renderer = inject(Renderer2);
  router = inject(Router);
  trs = inject(TechReportService);
  chatService = inject(ChatService);
  inboxService = inject(InboxService);
  sessionService = inject(SessionService);
  usageService = inject(UsageService);
  api = inject(ApiService);
  workspaceService = inject(WorkspaceService);
  inboxDrawerVisible = signal(false);
  organizationDialogVisible = signal(false);
  createOrganizationDialogVisible = signal(false);

  userMenuItems: MenuItem[] = [
    {
      label: 'Account Settings',
      icon: 'pi pi-cog',
      routerLink: ['/app/settings/user-account'],
    },
    { separator: true },
    {
      label: 'Sign Out',
      icon: 'pi pi-sign-out',
      command: () => this.sessionService.signOut(),
    },
  ];
  version = version;

  showCreditMeter = computed(() => {
    // TODO: Do not show unless user is actually in a workspace (workspace_id in route)
    if (!this.router.url.includes('/ws/')) {
      console.log('not in a workspace');
      return false;
    }
    const activeWorkspace = this.workspaceService.activeWorkspace();
    console.log('activeWorkspace', activeWorkspace);
    return (
      // Only show the credit meter when there is an active workspace AND the
      // account is credit-gated (hide it for unlimited Enterprise/Admin accounts).
      activeWorkspace &&
      !activeWorkspace.billing_account_is_credit_unlimited
    );
  })

  getLetterIcon(name: string) {
    if (!name) {
      return 'fa-solid fa-user';
    }
    const firstLetter = name[0].toLowerCase();
    return `fa-solid fa-${firstLetter}`;
  }

  overlayMenuOpenSubscription: Subscription;
  tabOpenSubscription: Subscription;
  tabCloseSubscription: Subscription;
  menuOutsideClickListener: any;
  menuScrollListener: any;

  // auth0User = computed(() => this.sessionService.state().auth0User);
  

  onWindowScroll(event: Event) {
    this.trs.onScroll(event);
  }

  constructor() {

    this.router.events.pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.hideMenu();
      });

    this.tabOpenSubscription = this.layoutService.tabOpen$.subscribe(tab => {
      this.router.navigate(tab.routerLink);
      this.layoutService.openTab(tab);
    });

    this.tabCloseSubscription = this.layoutService.tabClose$.subscribe((event: TabCloseEvent) => {
      if (this.router.isActive(event.tab.routerLink[0], { paths: 'subset', queryParams: 'subset', fragment: 'ignored', matrixParams: 'ignored' })) {
        const tabs = this.layoutService.tabs;

        if (tabs.length > 1) {
          if (event.index === (tabs.length - 1))
            this.router.navigate(tabs[tabs.length - 2].routerLink);
          else
            this.router.navigate(tabs[event.index + 1].routerLink);
        }
        else {
          this.router.navigate(['/']);
        }
      }

      this.layoutService.closeTab(event.index);
    });


  }


  async ngOnInit() {
    this.inboxService.loadInbox();
    if (this.sessionService.userIsAdmin()) {
      this.userMenuItems.push({
        label: 'Admin',
        icon: 'pi pi-cog',
        routerLink: ['/app/admin'],
      });
    }
  }

  blockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.add('blocked-scroll');
    }
    else {
      document.body.className += ' blocked-scroll';
    }
  }

  unblockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.remove('blocked-scroll');
    }
    else {
      document.body.className = document.body.className.replace(new RegExp('(^|\\b)' +
        'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
    }
  }

  hideMenu() {
    // this.layoutService.state.overlayMenuActive = false;
    // this.layoutService.state.staticMenuMobileActive = false;
    // this.layoutService.state.menuHoverActive = false;
    this.menuService.reset();
    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
      this.menuOutsideClickListener = null;
    }

    if (this.menuScrollListener) {
      this.menuScrollListener();
      this.menuScrollListener = null;
    }

    this.unblockBodyScroll();
  }

  ngOnDestroy() {
    if (this.overlayMenuOpenSubscription) {
      this.overlayMenuOpenSubscription.unsubscribe();
    }

    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
    }

    if (this.tabOpenSubscription) {
      this.tabOpenSubscription.unsubscribe();
    }

    if (this.tabCloseSubscription) {
      this.tabCloseSubscription.unsubscribe();
    }
  }



  searchActive = false;
  onMenuButtonClick() {
    this.layoutService.toggleSidebarActive();
  }

  // @ViewChild('searchinput') searchInput!: ElementRef;
  // activateSearch() {
  //   this.searchActive = true;
  //   setTimeout(() => {
  //     this.searchInput.nativeElement.focus();
  //   }, 100);
  // }

  deactivateSearch() {
    this.searchActive = false;
  }

  removeTab(event: MouseEvent, item: MenuItem, index: number) {
    this.layoutService.onTabClose(item, index);
    event.preventDefault();
  }

  onWorkspaceChange(id: string) {
    this.workspaceService.setActiveWorkspace(id);
  }

  createAccountForm = new FormGroup({
    name: new FormControl('', [Validators.required]),
    is_default: new FormControl(false),
  });

  createAccountLoading = signal(false);
  createAccountSuccess = signal(false);
  async createAccount() {
    if (this.createAccountForm.invalid || this.createAccountLoading() || this.createAccountSuccess()) {
      return;
    }
    this.createAccountSuccess.set(false);
    this.createAccountLoading.set(true);
    try {
      const newWorkspaceId: string = await this.api.apost<string>('ws/create', {
        name: this.createAccountForm.value.name,
        is_default: this.createAccountForm.value.is_default,
      });
      this.workspaceService.setActiveWorkspace(newWorkspaceId);
      this.createAccountSuccess.set(true);
    } catch (err) {
      console.error('Failed to create account', err);
      alert('Something went wrong, please try again later');
      throw err;
    } finally {
      this.createAccountForm.reset();
      this.createAccountLoading.set(false);
    }
  }

  onManageAccounts() {
    this.layoutService.createAccountDialogVisible.set(false);
    this.router.navigate(['/app/billing/accounts']);
  }

  openOrganizationDialog() {
    this.organizationDialogVisible.set(true);
  }

  onSelectOrganization(org: IOrganizationSummary) {
    this.sessionService.setActiveOrganization(org.id);
    this.organizationDialogVisible.set(false);
    this.router.navigate(['/']);
  }

  onManageOrganizations() {
    this.organizationDialogVisible.set(false);
    this.router.navigate(['/app/organizations']);
  }

  onManageSelectedOrganization() {
    const activeOrganizationId = this.sessionService.activeOrganizationId();
    if (!activeOrganizationId) return;
    this.organizationDialogVisible.set(false);
    this.router.navigate(['/app/organizations', activeOrganizationId]);
  }

  onCreateOrganization() {
    this.organizationDialogVisible.set(false);
    this.createOrganizationDialogVisible.set(true);
  }

}

